// Choosing a company from the admin company register, shared by the payment
// and credit-adjustment tools.
import { NativeSelect, Stack, TextInput } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { FormError } from "../../shared/ui/Page";

type Company = Schemas["Company"];

// The same query as the company register's "All" filter, so both share one cache entry.
export const COMPANIES_KEY = ["admin", "companies", "all"] as const;

export function useAdminCompanies(enabled: boolean) {
  return useQuery({
    queryKey: COMPANIES_KEY,
    enabled,
    queryFn: () => unwrap(api.GET("/admin/companies", { params: { query: { limit: 200 } } })),
  });
}

/** CompanyPicker narrows the company register by name or reference and picks one. */
export function CompanyPicker({
  value,
  onChange,
  enabled = true,
}: {
  value: string;
  onChange: (c: Company | null) => void;
  enabled?: boolean;
}) {
  const { t } = useTranslation();
  const companies = useAdminCompanies(enabled);
  const [q, setQ] = useState("");
  const all = companies.data?.items ?? [];
  const needle = q.trim().toLowerCase();
  const matches = all.filter(
    (c) =>
      c.id === value || !needle || c.name.toLowerCase().includes(needle) || c.public_ref.toLowerCase().includes(needle),
  );
  return (
    <Stack gap="xs">
      <TextInput
        label={t("admin_payments.search_company")}
        description={t("admin_payments.search_company_hint")}
        value={q}
        onChange={(e) => setQ(e.currentTarget.value)}
      />
      {companies.isError && <FormError message={errorMessage(companies.error, t("error.generic"))} />}
      <NativeSelect
        label={t("admin_payments.company")}
        value={value}
        onChange={(e) => onChange(all.find((c) => c.id === e.currentTarget.value) ?? null)}
        required
        disabled={companies.isPending}
        data={[
          { value: "", label: matches.length ? t("admin_payments.choose_company") : t("admin_payments.no_companies") },
          ...matches.map((c) => ({ value: c.id, label: `${c.name} (${c.public_ref})` })),
        ]}
      />
    </Stack>
  );
}
